import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Minus, Star } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import JsonLd from "@/components/site/JsonLd";
import { HS_LEVELS, HS_FAQS } from "@/lib/homeSurveys";
import { BRAND, SERVICE_TOWNS } from "@/lib/content";
import { AREAS } from "@/lib/areas";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function HomeSurveys() {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: "RICS Home Surveys (Levels 1, 2 & 3) in Lincolnshire",
        serviceType: "RICS Home Survey",
        provider: {
          "@type": "ProfessionalService",
          name: "Declan Bishop — Property Consultant",
          url: "https://dbpropertyconsultant.co.uk",
          telephone: "+44 7947 981 727",
        },
        areaServed: SERVICE_TOWNS.map((t) => ({ "@type": "City", name: t })),
        description:
          "RICS Home Surveys to the current Home Survey Standard at Levels 1, 2 and 3, provided survey-only (no valuations), throughout Lincolnshire.",
        url: "https://dbpropertyconsultant.co.uk/home-surveys",
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "RICS Home Survey Levels",
          itemListElement: HS_LEVELS.map((l) => ({
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: l.name, description: l.bestFor },
          })),
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: HS_FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://dbpropertyconsultant.co.uk/" },
          { "@type": "ListItem", position: 2, name: "RICS Home Surveys", item: "https://dbpropertyconsultant.co.uk/home-surveys" },
        ],
      },
    ],
  };

  return (
    <SiteLayout
      pageTitle="RICS Home Surveys Level 1, 2 & 3 — Lincolnshire"
      pageDescription="RICS Home Surveys at Levels 1, 2 and 3 across Lincolnshire — Lincoln, Boston, Grantham, Louth, Horncastle, Sleaford, Spalding, Skegness and beyond. Survey only (no valuations), plain-English reports, follow-up call included."
      pagePath="/home-surveys"
    >
      <JsonLd id="hs-schema" data={schema} />

      <section data-testid="hs-hero" className="bg-brand-cream border-b border-brand-navy/10">
        <div className="container-narrow pt-16 pb-16 md:pt-24 md:pb-20">
          <div className="overline" data-testid="hs-overline">RICS Home Surveys · Lincolnshire</div>
          <h1
            data-testid="hs-headline"
            className="mt-6 font-serif text-[44px] sm:text-6xl lg:text-7xl text-brand-navy leading-[0.98] tracking-tight max-w-4xl"
          >
            RICS Home Surveys — <span className="italic font-light text-brand-champagne">Levels 1, 2 &amp; 3.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-brand-navy/70 leading-relaxed">
            Buying a home anywhere in Lincolnshire? Choose the right depth of inspection —
            from a clear condition snapshot to a full, in-depth building analysis — delivered
            to the current RICS Home Survey Standard with plain-English reporting.
          </p>
          <p data-testid="hs-no-valuation-note" className="mt-5 max-w-2xl text-sm text-brand-navy/60 leading-relaxed">
            Surveys are provided on a <strong className="text-brand-navy">survey-only basis — valuations are not included</strong>.
            If you need a market or mortgage valuation, that must come from an RICS Registered
            Valuer, typically arranged via your lender.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="hs-cta-primary"
              className="inline-flex items-center gap-3 bg-brand-navy text-brand-cream px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy-soft transition-colors"
            >
              Get a Fixed Quote
              <ArrowUpRight size={16} />
            </Link>
            <a
              href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}
              data-testid="hs-cta-call"
              className="inline-flex items-center gap-3 border border-brand-navy/30 text-brand-navy px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy hover:text-brand-cream transition-colors"
            >
              Call {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      <section data-testid="hs-levels" className="bg-white border-b border-brand-navy/10">
        <div className="container-narrow py-20 md:py-28">
          <div className="overline">Choose your level</div>
          <h2 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy tracking-tight max-w-2xl">
            Three levels. One honest recommendation.
          </h2>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-px bg-brand-navy/10 border border-brand-navy/10">
            {HS_LEVELS.map((l) => (
              <div
                key={l.level}
                data-testid={`hs-level-${l.level}`}
                className={`relative p-8 md:p-10 ${l.popular ? "bg-brand-navy text-brand-cream" : "bg-white"}`}
              >
                {l.popular && (
                  <div className="absolute top-6 right-6 inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-brand-champagne">
                    <Star size={12} /> Most chosen
                  </div>
                )}
                <div className={`font-serif text-5xl ${l.popular ? "text-brand-champagne" : "text-brand-champagne"}`}>
                  {l.level}
                </div>
                <h3 className={`mt-4 font-serif text-2xl tracking-tight ${l.popular ? "text-brand-cream" : "text-brand-navy"}`}>
                  {l.name}
                </h3>
                <div className={`mt-1 text-xs italic ${l.popular ? "text-brand-cream/60" : "text-brand-navy/50"}`}>
                  {l.formerly}
                </div>
                <p className={`mt-4 text-sm leading-relaxed ${l.popular ? "text-brand-cream/80" : "text-brand-navy/70"}`}>
                  <strong className={l.popular ? "text-brand-cream" : "text-brand-navy"}>Best for:</strong> {l.bestFor}
                </p>
                <ul className="mt-6 space-y-2.5">
                  {l.includes.map((item) => (
                    <li key={item} className={`flex items-start gap-2.5 text-sm leading-relaxed ${l.popular ? "text-brand-cream/85" : "text-brand-navy/75"}`}>
                      <Check size={15} className="mt-0.5 shrink-0 text-brand-champagne" />
                      {item}
                    </li>
                  ))}
                  {l.excludes.map((item) => (
                    <li key={item} className={`flex items-start gap-2.5 text-sm leading-relaxed ${l.popular ? "text-brand-cream/50" : "text-brand-navy/45"}`}>
                      <Minus size={15} className="mt-0.5 shrink-0" />
                      Not included: {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`hs-level-${l.level}-cta`}
                  className={`mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors ${
                    l.popular ? "text-brand-champagne hover:text-brand-cream" : "text-brand-navy ink-link"
                  }`}
                >
                  Enquire about Level {l.level}
                  <ArrowUpRight size={13} />
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-10 text-sm text-brand-navy/60 max-w-3xl leading-relaxed">
            Not sure which level fits the property? Send me the listing — I&apos;ll recommend
            honestly, including when the cheaper level is the right one. For focused
            investigations, see{" "}
            <Link to="/condition-surveys" className="ink-link font-medium text-brand-navy" data-testid="hs-cs-link">
              condition surveys
            </Link>{" "}
            and{" "}
            <Link to="/services#roof-surveys" className="ink-link font-medium text-brand-navy" data-testid="hs-roof-link">
              roof surveys
            </Link>.
          </p>
        </div>
      </section>

      <section data-testid="hs-faq" className="bg-brand-cream border-b border-brand-navy/10">
        <div className="container-narrow py-20 md:py-28">
          <div className="grid grid-cols-12 gap-y-12 md:gap-x-12">
            <div className="col-span-12 md:col-span-4">
              <div className="overline">Common questions</div>
              <h2 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy leading-tight tracking-tight">
                RICS surveys, explained plainly.
              </h2>
              <p className="mt-6 text-sm text-brand-navy/65 leading-relaxed">
                Can&apos;t find your answer? Call {BRAND.phone} — a straight answer is always free.
              </p>
            </div>
            <div className="col-span-12 md:col-span-8">
              <Accordion type="single" collapsible className="w-full" data-testid="hs-faq-accordion">
                {HS_FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-brand-navy/10">
                    <AccordionTrigger
                      data-testid={`hs-faq-question-${i}`}
                      className="text-left font-serif text-lg md:text-xl text-brand-navy hover:no-underline hover:text-brand-champagne py-6"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm md:text-base text-brand-navy/75 leading-relaxed pb-6">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="hs-coverage" className="bg-white">
        <div className="container-narrow py-20 md:py-28">
          <div className="overline">Coverage</div>
          <h2 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy tracking-tight max-w-3xl">
            RICS home surveys throughout Lincolnshire.
          </h2>
          <p className="mt-6 max-w-2xl text-sm md:text-base text-brand-navy/65 leading-relaxed">
            Level 1, 2 and 3 surveys are available in every town and village we cover —
            towns with a local guide are linked below.
          </p>
          <div data-testid="hs-towns" className="mt-10 flex flex-wrap gap-x-6 gap-y-3">
            {SERVICE_TOWNS.map((t) => {
              const area = AREAS.find((a) => a.name === t);
              return area ? (
                <Link
                  key={t}
                  to={`/areas/${area.slug}`}
                  data-testid={`hs-town-${area.slug}`}
                  className="text-sm font-medium text-brand-navy ink-link"
                >
                  {t}
                </Link>
              ) : (
                <span key={t} className="text-sm text-brand-navy/60">{t}</span>
              );
            })}
          </div>
          <div className="mt-14">
            <Link
              to="/contact"
              data-testid="hs-coverage-cta"
              className="inline-flex items-center gap-3 bg-brand-navy text-brand-cream px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy-soft transition-colors"
            >
              Request a Survey Quote
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
